<template>
  <div class="m-date-picker">
    <m-button
      ghost
      icon="fa-light fa-chevron-left"
      icon-only
      :label="`Previous ${view}`"
      @click="prev"
    />
    <m-button ref="calendarButton" class="date-text-container" flat @click="showForm = !showForm">
      <span class="date-text">
        <span class="sr-only">Change Date</span>
        {{ showText }}
      </span>
    </m-button>

    <m-button
      ghost
      icon="fa-light fa-chevron-right"
      icon-only
      :label="`Next ${view}`"
      @click="next"
    />
  </div>

  <Teleport to="body">
    <div v-if="showForm" ref="popupEl" :style="popupStyle">
      <m-calendar-grid
        :model-value="calendarModel"
        :min-date="resolvedMin"
        :max-date="resolvedMax"
        :initial-view="initialView"
        @update:model-value="onModelValue"
        @month-selected="onMonthSelected"
        @close="showForm = false"
      >
        <template #header-actions>
          <m-button
            icon="fa-solid fa-xmark"
            icon-only
            color="secondary"
            sr-label="Close"
            :outline="false"
            :small="true"
            :dense="true"
            class="borderless"
            @click="showForm = false"
          />
        </template>
      </m-calendar-grid>
    </div>
  </Teleport>
</template>
<script lang="ts">
export const enum IncrementStep {
  day = "day",
  week = "week",
  month = "month",
  year = "year",
}

export default {};
</script>
<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue";
import { isDate, isNumber, isString, useVModel } from "vue-composable";
import { getMaxDate, getMinDate } from "../../../../utils/helpers";
import { addToDate, formatDatePattern, subtractFromDate } from "../../../../utils/date";
import { usePopupPosition } from "../../../../composables/usePopupPosition";
import MButton from "../../MButton";
import MCalendarGrid from "../MCalendarGrid/MCalendarGrid.vue";

type DateInput = string | number | Date;
type DateRange = { from: DateInput; to: DateInput };

const props = defineProps({
  modelValue: [Date, String, Object, Number] as unknown as () =>
    | string
    | number
    | Date
    | { from: string; to: string },
  minDate: {
    type: [Date, String],
    default() {
      return new Date(1000, 1, 1);
    },
  },
  maxDate: {
    type: [Date, String],
    default() {
      return new Date(new Date().getFullYear() + 100, 1, 1);
    },
  },

  view: {
    type: String as () => IncrementStep,
    default() {
      return "day";
    },
  },
});

const showForm = ref(false);
const calendarButton = ref(null);

// Return focus to the opener when the calendar closes so keyboard flow continues naturally.
watch(showForm, (open) => {
  if (!open) {
    nextTick(() => (calendarButton.value as any)?.focus());
  }
});

const { popupEl, popupStyle } = usePopupPosition({
  isOpen: showForm,
  getTrigger: () => {
    const ref = calendarButton.value as any;
    return (ref?.$el ?? ref) as HTMLElement | null;
  },
});

// Parse the various inputs we receive (YYYY-MM-DD / DD/MM/YYYY strings, Date, epoch) into a
// local Date without timezone drift, so a date-only string keeps its calendar day.
function coerceDate(v: DateInput): Date {
  if (v instanceof Date) return v;
  if (typeof v === "number") return new Date(v);
  const part = v.split(" ")[0];
  const iso = part.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return new Date(+iso[1], +iso[2] - 1, +iso[3]);
  const uk = part.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (uk) return new Date(+uk[3], +uk[2] - 1, +uk[1]);
  return new Date(v);
}

function fmt(v: DateInput, pattern: string): string {
  return formatDatePattern(coerceDate(v), pattern);
}

const dateValue = useVModel(props, "modelValue");

const dateText = computed<string | { from: string; to: string }>({
  get() {
    if (!dateValue.value) return "";
    if (isDate(dateValue.value) || isString(dateValue.value) || isNumber(dateValue.value)) {
      return fmt(dateValue.value as DateInput, "DD/MM/YYYY");
    }

    const range = dateValue.value as DateRange;
    return {
      from: fmt(range.from, "DD/MM/YYYY"),
      to: fmt(range.to, "DD/MM/YYYY"),
    };
  },
  set(v: DateInput | DateRange) {
    if (isDate(v) || isString(v) || isNumber(v)) {
      dateValue.value = fmt(v as DateInput, "YYYY-MM-DD");
    } else if (v !== null) {
      dateValue.value = {
        from: fmt(v.from, "YYYY-MM-DD"),
        to: fmt(v.to, "YYYY-MM-DD"),
      };
    }
  },
});

const resolvedMin = computed(() => getMinDate(props.minDate, false));
const resolvedMax = computed(() => getMaxDate(props.maxDate, false));

// The shared grid only handles a single DD/MM/YYYY value; for a range we anchor it on `from`.
const calendarModel = computed(() => {
  const v = dateText.value;
  return isString(v) ? v : v.from;
});

// Month view picks a whole month, so open the grid on its month chooser.
const initialView = computed<"days" | "months">(() =>
  props.view === IncrementStep.month ? "months" : "days",
);

function formatDate(d: DateInput, showYear = true) {
  return fmt(d, `ddd DD MMM${showYear ? " YYYY" : ""}`);
}

function formatDisplayDate(d: DateInput, type: string) {
  switch (type) {
    case "month": {
      return fmt(d, "MMM YYYY");
    }
    case "week": {
      const n = coerceDate(d);

      return "w/c " + fmt(subtractFromDate(n, { days: n.getDay() - 1 }), "DD MMM YYYY");
    }

    default:
    case "day": {
      return fmt(d, "ddd DD MMM YYYY");
    }
  }
}

const showText = computed(() => {
  if (!props.modelValue) return "";

  if (isDate(props.modelValue) || isString(props.modelValue) || isNumber(props.modelValue)) {
    return formatDisplayDate(props.modelValue as DateInput, props.view);
  }
  const range = props.modelValue as DateRange;
  return `${formatDate(range.from, false)} - ${formatDate(range.to, false)}`;
});

function doStep(forward: boolean) {
  const v = dateValue.value;
  if (!v) return;
  let step = props.view === IncrementStep.week ? 7 : 1;

  if (!forward) {
    step = -step;
  }

  const options =
    props.view === IncrementStep.month
      ? { months: step }
      : props.view === IncrementStep.year
      ? { years: step }
      : { days: step };

  if (isDate(v) || isString(v) || isNumber(v)) {
    dateValue.value = fmt(addToDate(coerceDate(v as DateInput), options), "YYYY-MM-DD");
  } else {
    const range = v as DateRange;
    dateValue.value = {
      from: fmt(addToDate(coerceDate(range.from), options), "YYYY-MM-DD"),
      to: fmt(addToDate(coerceDate(range.to), options), "YYYY-MM-DD"),
    };
  }
}

function next() {
  doStep(true);
}

function prev() {
  doStep(false);
}

// Month view: picking a month sets the range to that whole month and closes.
function onMonthSelected({ year, month }: { year: number; month: number }) {
  if (props.view !== IncrementStep.month) return;

  const from = new Date(year, month, 1);
  const to = addToDate(from, { months: 1, days: -1 });

  dateValue.value = {
    from: fmt(from, "YYYY-MM-DD"),
    to: fmt(to, "YYYY-MM-DD"),
  };

  showForm.value = false;
}

// Single-date views: the grid emits the chosen day as DD/MM/YYYY.
function onModelValue(value: string) {
  dateText.value = value;
  showForm.value = false;
}
</script>
<style lang="scss">
.m-date-picker {
  user-select: none;

  color: var(--text-color);
  display: flex;
  gap: 0.25rem;
  font-size: 14px;

  align-self: center;
  align-items: center;

  .date-text-container {
    width: 150px;
    padding: 0;
    display: flex;

    justify-content: center;
  }

  // ENG-20661 q-btn → m-button forces bold; date text should not be bold
  .date-text {
    font-weight: normal;
  }

  i {
    align-content: center;
    width: 40px;
  }

  span {
    display: flex;
    align-items: center;

    padding: 0 0.25rem;
  }

  i,
  span {
    height: 35px;
    cursor: pointer;

    &:hover {
      background: #e7eaee;
    }
  }
}
</style>
